/* global window, document */

import videojs from 'video.js';
import get from 'lodash/get';

/**
 * Class representing Google DAI Tracker.
 */
class DAITracker {
  /**
   * Create a DAITracker instance.
   */
  constructor(encore) {
    this.encore = encore;
    this.adUiElement = document.getElementById('adUiElement');
    this.streamManager = null;
  }

  /**
   * Stream load handler. Sets live stream as source.
   */
  loadStreamHandler(event) {
    if (!this.encore.source.isNgDai) {
      const daiStreamUrl = event.getStreamData().url;

      if (!daiStreamUrl) {
        videojs.log.error('DAI: stream data empty');
        return;
      }

      this.encore.source.setLiveStreamSource(daiStreamUrl);
      videojs.log(`DAI: loaded stream [${daiStreamUrl}]`);
      return;
    }

    if (!event) {
      const backupStreamUrl = this.encore.source.ngDaiBackupStream;
      this.encore.source.setLiveStreamSource(backupStreamUrl);
      videojs.log(`ngDAI: loaded backup stream [${backupStreamUrl}]`);
      return;
    }

    const daiStreamId = event.getStreamData().streamId;
    const daiStreamUrl = this.encore.source.ngDaiStreamUrl.replace('[[STREAMID]]', daiStreamId);

    this.encore.source.setLiveStreamSource(daiStreamUrl);
    videojs.log(`ngDAI: loaded stream [${daiStreamUrl}]`);
  }

  /**
   * Stream load error handler.
   */
  loadStreamErrorHandler() {
    window.HRST_DAI_READY = true;

    if (!this.encore.source.isNgDai) {
      videojs.log('DAI: could not load stream');
    } else {
      this.loadStreamHandler();
    }
  }

  /**
   * Ad break started handler. Shows learn more button.
   */
  adBreakStartHandler() {
    setTimeout(() => this.encore.player.bigPlayButton.hide(), 500);
    this.adUiElement.classList.remove('hidden');
    videojs.log('DAI: ad break started');
  }

  /**
   * Ad break ended handler. Hides learn more button.
   */
  adBreakEndHandler() {
    this.adUiElement.classList.add('hidden');
    this.encore.player.bigPlayButton.show();
    videojs.log('DAI: ad break ended');
  }

  /**
   * Click handler. Pauses video.
   */
  clickHandler() {
    this.encore.player.pause();
    videojs.log('DAI: click captured');
  }

  /**
   * Set up stream manager and register ad break and click events.
   */
  setupStreamManager() {
    this.streamManager = new window.google.ima.dai.api.StreamManager(
      this.encore.player.el().children[0], this.adUiElement);

    this.streamManager.addEventListener(
      window.google.ima.dai.api.StreamEvent.Type.AD_BREAK_STARTED,
      this.adBreakStartHandler.bind(this), false);

    this.streamManager.addEventListener(
      window.google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED,
      this.adBreakEndHandler.bind(this), false);

    this.streamManager.addEventListener(
      window.google.ima.dai.api.StreamEvent.Type.CLICK,
      this.clickHandler.bind(this), false);
  }

  /**
   * Set up track events to update ID3 tags on cue changes.
   */
  setupCueEvents() {
    this.encore.player.one('loadedmetadata', () => {
      const tracks = this.encore.player.textTracks();
      let timedMetadataTrack;

      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].label === 'Timed Metadata') {
          timedMetadataTrack = tracks[i];
        }
      }

      if (!timedMetadataTrack) {
        videojs.log.error('No timed metadata cue track found');
        return;
      }

      timedMetadataTrack.addEventListener('cuechange', () => {
        const key = get(timedMetadataTrack, 'activeCues.0.value.key', '');
        const data = get(timedMetadataTrack, 'activeCues.0.value.data', '');

        if (!this.streamManager || !key || !data) {
          return;
        }

        const timedMetadata = {
          [key]: data,
          width: Math.floor(this.encore.player.currentWidth()),
          height: Math.floor(this.encore.player.currentHeight()),
          duration: Number.POSITIVE_INFINITY,
        };

        this.streamManager.onTimedMetadata(timedMetadata);
      });
    });
  }

  /**
   * Registers stream events and requests a live stream.
   * @returns {Promise}
   */
  requestLiveStream() {
    const isNgDai = this.encore.source.isNgDai;
    const loadedEvent = isNgDai ? window.google.ima.dai.api.StreamEvent.Type.STREAM_INITIALIZED : window.google.ima.dai.api.StreamEvent.Type.LOADED;

    const requestPromise = new Promise(resolve => {
      this.streamManager.addEventListener(loadedEvent,
        event => {
          this.loadStreamHandler(event);
          resolve();
        }, false);
      this.streamManager.addEventListener(window.google.ima.dai.api.StreamEvent.Type.ERROR,
        () => {
          this.loadStreamErrorHandler();
          resolve();
        }, false);
    });

    let streamRequest = null;

    if (isNgDai) {
      streamRequest = new window.google.ima.dai.api.PodStreamRequest();
      streamRequest.networkCode = this.encore.source.ngDaiNetworkCode;
      streamRequest.customAssetKey = this.encore.source.ngDaiAssetKey;
      streamRequest.apiKey = '';
      streamRequest.attemptPreroll = false;
    } else {
      streamRequest = new window.google.ima.dai.api.LiveStreamRequest();
      streamRequest.assetKey = this.encore.source.daiAssetId;
      streamRequest.apiKey = '';
      streamRequest.attemptPreroll = false;
    }

    if (this.encore.settings.doNotSellStatus) {
      streamRequest.adTagParameters = { rdp: 1 };
      if (window.HTV_DAI_RDP_CUST) {
        streamRequest.adTagParameters.cust_params = encodeURI(window.HTV_DAI_RDP_CUST);
      }
    }

    this.streamManager.requestStream(streamRequest);

    return requestPromise;
  }
}

export default DAITracker;
